import styled from 'styled-components/macro';
import { MuiPaletteColor, BaseReactComponent } from '../../../types';
import { useIntl } from 'react-intl';

import { lighten, darken, Chip, SvgIconProps, ChipProps } from '@mui/material';
import { CancelOutlined } from '@mui/icons-material';
import { KeyboardEvent } from 'react';

export interface HiChipBaseProps extends BaseReactComponent {
  /**
   * Type of the component.
   */
  type: 'info' | 'removable' | 'action';
  /**
   * The content of the component.
   */
  label: string;
  /**
   * The color of the component. It supports theme colors.
   */
  color?: MuiPaletteColor;
  /**
   * Icon element placed before the label.
   * @default info
   */
  icon?: React.ComponentType<SvgIconProps>;
  /**
   * The size of the component.
   * @default medium
   */
  size?: 'small' | 'medium';
  /**
   * Icon element clickable placed after the label.
   */
  actionIcon?: React.ComponentType<SvgIconProps> | never;
  /**
   * Title displayed when hover the actionIcon.
   */
  actionTitle?: string | never;
  /**
   * Callback fired when the action icon is clicked.
   */
  onAction?: ChipProps['onDelete'] | never;
  /**
   * Override Mui Chip props
   * (https://mui.com/material-ui/api/chip/).
   */
  ChipProps?: ChipProps;
}

export interface InfoChip extends HiChipBaseProps {
  type: 'info';
  actionIcon?: never;
  actionTitle?: never;
  onAction?: never;
}

export interface RemovableChip extends HiChipBaseProps {
  type: 'removable';
  actionIcon?: never;
  actionTitle?: string;
  onAction: ChipProps['onDelete'];
}

export interface ActionChip extends HiChipBaseProps {
  type: 'action';
  actionIcon: React.ComponentType<SvgIconProps>;
  actionTitle: string;
  onAction: ChipProps['onDelete'];
}

export type HiChipProps = InfoChip | RemovableChip | ActionChip;

/**
 * The Chip component is used for different purposes and its styles depends on it:
 * - `informative`: Display an information.
 * - `removable`: When the chip can be removed.
 * - `action`: When you can perform an action.
 *
 * **Icons** and **colors** should be understandable and help to understand the information.
 *
 * It extends Mui's `Chip` component.
 */
const HiChip = ({
  className,
  label,
  color,
  icon: IconComponent,
  size = 'medium',
  type,
  onAction,
  actionTitle,
  actionIcon: ActionIconComponent = CancelOutlined,
  ChipProps,
  ...other
}: HiChipProps) => {
  const intl = useIntl();

  // If "space" pressed when Chip is focused, call onAction
  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.code === 'Space' && onAction) {
      event.preventDefault();
      onAction(event);
    }
  };

  return (
    <StyledChip
      {...other}
      {...ChipProps}
      className={`type-${type} ` + (className ?? '')}
      classes={{
        icon: 'HiChip-iconComponent',
        deleteIcon: 'HiChip-actionIcon'
      }}
      color={color}
      icon={IconComponent && <IconComponent />}
      label={label}
      title={label}
      size={size}
      variant={type === 'info' ? 'outlined' : 'filled'}
      onDelete={type !== 'info' ? onAction : undefined}
      deleteIcon={
        ActionIconComponent && (
          <ActionIconComponent
            titleAccess={
              type === 'removable'
                ? actionTitle || intl.formatMessage({ id: 'HiChip.remove' })
                : actionTitle
            }
          />
        )
      }
      onKeyDown={handleKeyDown}
    />
  );
};

const StyledChip = styled(Chip).attrs(({ color = 'info' }) => ({
  color: color
}))`
  &.type-info,
  &.type-removable {
    color: ${({ theme, color }) =>
      color === 'info'
        ? theme.palette[color].dark
        : darken(theme.palette[color].main, 0.6)};

    .HiChip-iconComponent {
      color: ${({ theme, color }) => theme.palette[color].main};
    }
  }

  &.type-removable {
    background-color: ${({ theme, color }) =>
      lighten(theme.palette[color].main, 0.9)};

    .HiChip-actionIcon {
      color: ${({ theme, color }) => theme.palette[color].main};

      &:hover {
        color: ${({ theme, color }) => theme.palette[color].dark};
      }
    }

    &:focus-visible {
      background-color: ${({ theme, color }) =>
        lighten(theme.palette[color].main, 0.7)};
    }
  }

  &.type-action {
    .HiChip-iconComponent {
      opacity: 0.7;
    }

    .HiChip-actionIcon {
      color: ${({ theme, color }) => theme.palette[color].contrastText};

      &:hover {
        opacity: 0.7;
      }
    }
  }

  .HiChip-actionIcon:hover {
    transform: scale(1.2);
    transition: ease-in 0.1s;
  }
`;

export default HiChip;
